import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEmployees } from 'services/employees';
import { getContractors } from 'services/contractors';
import { getDepartments } from 'services/departments';
import { updatePayroll, previewPayroll, approvePayroll } from 'services/payrolls';
import { runAccruals } from 'services/timeOff';

const OFF_CYCLE_REASONS = [
    { label: 'Bonus',                    value: 'bonus' },
    { label: 'Commission',               value: 'commission' },
    { label: 'Missed Pay',               value: 'missed_pay' },
    { label: 'Termination / Final Pay',  value: 'termination' },
    { label: 'Expense Reimbursement',    value: 'reimbursement' },
    { label: 'Other',                    value: 'other' },
];

const fmt = (n) => n == null ? '-' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STEPS = { SELECT: 1, PAYMENTS: 2, PREVIEW: 3 };

export default function OffCyclePayrollModal({ payroll, onClose, onApproved }) {
    const queryClient = useQueryClient();
    const [step, setStep]             = useState(STEPS.SELECT);
    const [activeTab, setActiveTab]   = useState('employees');   // 'employees' | 'contractors'
    const [reason, setReason]         = useState('bonus');
    const [search, setSearch]         = useState('');

    // Employee state
    const [selectedEmpIds, setSelectedEmpIds]   = useState([]);
    const [empPayments, setEmpPayments]         = useState({});

    // Contractor state
    const [selectedContIds, setSelectedContIds] = useState([]);
    const [contPayments, setContPayments]       = useState({});

    const [previewData, setPreviewData]     = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [approved, setApproved]           = useState(false);
    const [errorMsg, setErrorMsg]           = useState('');

    // ── Data fetching ────────────────────────────────────────────────────────
    const { data: employeesRaw = [], isLoading: loadingEmps } = useQuery({ queryKey: ['employees'], queryFn: getEmployees });
    const employees = useMemo(() =>
        Array.isArray(employeesRaw) ? employeesRaw : (employeesRaw?.data ?? []), [employeesRaw]);

    const { data: contractorsRaw = [], isLoading: loadingConts } = useQuery({ queryKey: ['contractors'], queryFn: getContractors });
    const contractors = useMemo(() =>
        Array.isArray(contractorsRaw) ? contractorsRaw : (contractorsRaw?.data ?? []), [contractorsRaw]);

    const { data: departmentsRaw = [] } = useQuery({ queryKey: ['departments'], queryFn: getDepartments });
    const deptMap = useMemo(() => {
        const list = Array.isArray(departmentsRaw) ? departmentsRaw : (departmentsRaw?.data ?? []);
        return Object.fromEntries(list.map(d => [d.id, d.name]));
    }, [departmentsRaw]);

    // ── Derived lists ────────────────────────────────────────────────────────
    const activeEmployees    = employees.filter(e => e.active !== false);
    const activeContractors  = contractors.filter(c => c.active !== false);

    const filteredEmps  = activeEmployees.filter(e =>
        `${e.firstName || ''} ${e.lastName || ''}`.toLowerCase().includes(search.toLowerCase()));
    const filteredConts = activeContractors.filter(c =>
        `${c.firstName || ''} ${c.lastName || ''} ${c.businessName || ''}`.toLowerCase().includes(search.toLowerCase()));

    const selectedEmployees   = activeEmployees.filter(e => selectedEmpIds.includes(e.id));
    const selectedContractors = activeContractors.filter(c => selectedContIds.includes(c.id));
    const totalSelected       = selectedEmpIds.length + selectedContIds.length;

    // ── Toggle helpers ───────────────────────────────────────────────────────
    const toggleEmp  = (id) => setSelectedEmpIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const toggleCont = (id) => setSelectedContIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const toggleAllEmps  = () => setSelectedEmpIds(selectedEmpIds.length === filteredEmps.length ? [] : filteredEmps.map(e => e.id));
    const toggleAllConts = () => setSelectedContIds(selectedContIds.length === filteredConts.length ? [] : filteredConts.map(c => c.id));

    const setEmpPay  = (id, field, val) => setEmpPayments(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
    const setContPay = (id, field, val) => setContPayments(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));

    // ── Payment input label helpers ──────────────────────────────────────────
    const empPayLabel = () => {
        switch (reason) {
            case 'missed_pay':    return 'Hours';
            case 'bonus':         return 'Bonus Amount ($)';
            case 'commission':    return 'Commission Amount ($)';
            case 'reimbursement': return 'Reimbursement ($)';
            case 'termination':   return 'Severance ($)';
            default:              return 'Additional Hours';
        }
    };

    const contPayLabel = () => {
        switch (reason) {
            case 'reimbursement': return 'Reimbursement ($)';
            case 'termination':   return 'Severance ($)';
            default:              return 'Amount ($)';
        }
    };

    const isReimbType = reason === 'reimbursement' || reason === 'termination';

    // ── Save & preview mutation ──────────────────────────────────────────────
    const { mutate: doUpdate, isPending: updating } = useMutation({
        mutationFn: () => {
            const employeeItems = selectedEmployees.map(emp => {
                const p = empPayments[emp.id] || {};
                return {
                    employeeId: emp.id,
                    payments: [{
                        regularHours: reason === 'missed_pay'   ? (parseFloat(p.hours) || undefined) : undefined,
                        bonus:        reason === 'bonus'        ? (parseFloat(p.bonus) || undefined) : undefined,
                        commission:   reason === 'commission'   ? (parseFloat(p.commission) || undefined) : undefined,
                    }],
                    reimbursements: isReimbType && p.reimbursement > 0
                        ? [{ amount: parseFloat(p.reimbursement), description: reason === 'termination' ? 'Severance' : 'Expense Reimbursement' }]
                        : undefined,
                };
            });

            const contractorItems = selectedContractors.map(c => {
                const p = contPayments[c.id] || {};
                return {
                    contractorId: c.id,
                    grossPay:      !isReimbType ? (parseFloat(p.amount) || undefined) : undefined,
                    reimbursement: isReimbType  ? (parseFloat(p.reimbursement) || undefined) : undefined,
                    paymentMethod: c.defaultPaymentMethod || undefined,
                };
            });

            return updatePayroll({ payrollId: payroll.id, type: payroll.type || 'off_cycle', employeeItems, contractorItems });
        },
        onSuccess: async () => {
            setLoadingPreview(true);
            try {
                const data = await previewPayroll(payroll.id);
                setPreviewData(data);
                setStep(STEPS.PREVIEW);
            } catch {
                setErrorMsg('Failed to load preview.');
            } finally {
                setLoadingPreview(false);
            }
        },
        onError: () => setErrorMsg('Failed to save payment data.'),
    });

    // ── Approve mutation ─────────────────────────────────────────────────────
    const { mutate: doApprove, isPending: approving } = useMutation({
        mutationFn: () => approvePayroll(payroll.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payrolls'] });
            if (selectedEmployees.length > 0) {
                runAccruals({
                    payPeriodStart: payroll.startDate?.slice(0, 10),
                    payPeriodEnd:   payroll.endDate?.slice(0, 10),
                    employees: selectedEmployees.map(emp => ({
                        employeeId: emp.id,
                        hoursWorked: parseFloat(empPayments[emp.id]?.hours) || 0,
                    })),
                }).catch(() => {});
            }
            setApproved(true);
            if (onApproved) onApproved();
        },
        onError: () => setErrorMsg('Failed to approve payroll.'),
    });

    const reasonLabel = OFF_CYCLE_REASONS.find(r => r.value === reason)?.label || reason;

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 900, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                {/* Header */}
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h5 className="mb-0 fw-semibold">Off-Cycle Payroll</h5>
                        {payroll.startDate && (
                            <p className="mb-0 text-muted mt-1" style={{ fontSize: 13 }}>
                                Pay Date: {payroll.payDate?.slice(0, 10) || '—'}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
                </div>

                {/* Step indicator */}
                <div style={{ padding: '12px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', gap: 8, fontSize: 13 }}>
                    {[
                        { n: 1, label: 'Select Recipients' },
                        { n: 2, label: 'Enter Payments' },
                        { n: 3, label: 'Preview & Approve' },
                    ].map(s => (
                        <span key={s.n} style={{
                            padding: '4px 14px', borderRadius: 20, fontWeight: step === s.n ? 600 : 400,
                            background: step === s.n ? '#1B75F0' : step > s.n ? '#d4edda' : '#f0f0f0',
                            color: step === s.n ? '#fff' : step > s.n ? '#155724' : '#666',
                        }}>
                            {s.n}. {s.label}
                        </span>
                    ))}
                </div>

                <div style={{ padding: '24px' }}>
                    {errorMsg && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{errorMsg}</div>}

                    {/* ── STEP 1: Select Recipients ── */}
                    {step === STEPS.SELECT && (
                        <>
                            <div className="row g-3 mb-4">
                                <div className="col-md-6">
                                    <label className="fw-semibold mb-1" style={{ fontSize: 14 }}>Payment Reason*</label>
                                    <select className="form-select" value={reason} onChange={e => setReason(e.target.value)}>
                                        {OFF_CYCLE_REASONS.map(r => (
                                            <option key={r.value} value={r.value}>{r.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="fw-semibold mb-1" style={{ fontSize: 14 }}>Search</label>
                                    <input
                                        className="form-control"
                                        placeholder="Search by name..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Tabs */}
                            <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', marginBottom: 16, gap: 4 }}>
                                {[
                                    { key: 'employees',   label: 'Employees',   count: selectedEmpIds.length },
                                    { key: 'contractors', label: 'Contractors', count: selectedContIds.length },
                                ].map(tab => (
                                    <button
                                        key={tab.key}
                                        onClick={() => setActiveTab(tab.key)}
                                        style={{
                                            padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                            borderBottom: activeTab === tab.key ? '2px solid #1B75F0' : '2px solid transparent',
                                            marginBottom: -2,
                                            background: 'none',
                                            color: activeTab === tab.key ? '#1B75F0' : '#666',
                                        }}
                                    >
                                        {tab.label}
                                        {tab.count > 0 && (
                                            <span style={{ marginLeft: 6, background: '#1B75F0', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>
                                                {tab.count}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Employees tab */}
                            {activeTab === 'employees' && (
                                loadingEmps ? <p className="text-muted">Loading employees...</p> : (
                                    <div style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden' }}>
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                                            <thead style={{ background: '#f8f9fa' }}>
                                                <tr>
                                                    <th style={{ width: 40 }}>
                                                        <input type="checkbox"
                                                            checked={selectedEmpIds.length === filteredEmps.length && filteredEmps.length > 0}
                                                            onChange={toggleAllEmps} />
                                                    </th>
                                                    <th>Name</th>
                                                    <th>Pay Type</th>
                                                    <th>Department</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredEmps.length === 0 && (
                                                    <tr><td colSpan={4} className="text-center text-muted py-3">No employees found</td></tr>
                                                )}
                                                {filteredEmps.map(emp => (
                                                    <tr key={emp.id} style={{ cursor: 'pointer' }} onClick={() => toggleEmp(emp.id)}>
                                                        <td><input type="checkbox" checked={selectedEmpIds.includes(emp.id)} onChange={() => toggleEmp(emp.id)} onClick={e => e.stopPropagation()} /></td>
                                                        <td className="fw-medium">{emp.firstName} {emp.lastName}</td>
                                                        <td style={{ textTransform: 'capitalize' }}>{emp.payType || 'salary'}</td>
                                                        <td>{emp.departmentId ? (deptMap[emp.departmentId] || '-') : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            )}

                            {/* Contractors tab */}
                            {activeTab === 'contractors' && (
                                loadingConts ? <p className="text-muted">Loading contractors...</p> : (
                                    <div style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden' }}>
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                                            <thead style={{ background: '#f8f9fa' }}>
                                                <tr>
                                                    <th style={{ width: 40 }}>
                                                        <input type="checkbox"
                                                            checked={selectedContIds.length === filteredConts.length && filteredConts.length > 0}
                                                            onChange={toggleAllConts} />
                                                    </th>
                                                    <th>Name</th>
                                                    <th>Pay Type</th>
                                                    <th>Business Name</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredConts.length === 0 && (
                                                    <tr><td colSpan={4} className="text-center text-muted py-3">No contractors found</td></tr>
                                                )}
                                                {filteredConts.map(c => (
                                                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => toggleCont(c.id)}>
                                                        <td><input type="checkbox" checked={selectedContIds.includes(c.id)} onChange={() => toggleCont(c.id)} onClick={e => e.stopPropagation()} /></td>
                                                        <td className="fw-medium">{c.firstName} {c.lastName}</td>
                                                        <td style={{ textTransform: 'capitalize' }}>{c.payType || 'FlatFee'}</td>
                                                        <td>{c.businessName || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            )}

                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <span className="text-muted" style={{ fontSize: 13 }}>
                                    {selectedEmpIds.length > 0 && `${selectedEmpIds.length} employee${selectedEmpIds.length !== 1 ? 's' : ''}`}
                                    {selectedEmpIds.length > 0 && selectedContIds.length > 0 && ' · '}
                                    {selectedContIds.length > 0 && `${selectedContIds.length} contractor${selectedContIds.length !== 1 ? 's' : ''}`}
                                    {totalSelected === 0 && 'None selected'}
                                    {totalSelected > 0 && ` selected`}
                                </span>
                                <div className="d-flex gap-2">
                                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
                                    <button
                                        type="button"
                                        className="btn btn-sm"
                                        style={{ backgroundColor: '#1B75F0', color: '#fff' }}
                                        disabled={totalSelected === 0}
                                        onClick={() => setStep(STEPS.PAYMENTS)}
                                    >
                                        Next: Enter Payments
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {/* ── STEP 2: Enter Payments ── */}
                    {step === STEPS.PAYMENTS && (
                        <>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <span className="badge" style={{ background: '#fff3cd', color: '#856404', fontSize: 13, fontWeight: 500, padding: '5px 12px', borderRadius: 20 }}>
                                    Reason: {reasonLabel}
                                </span>
                                {selectedEmployees.length > 0 && <span className="text-muted" style={{ fontSize: 13 }}>{selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''}</span>}
                                {selectedContractors.length > 0 && <span className="text-muted" style={{ fontSize: 13 }}>{selectedContractors.length} contractor{selectedContractors.length !== 1 ? 's' : ''}</span>}
                            </div>

                            {/* Employee payment table */}
                            {selectedEmployees.length > 0 && (
                                <>
                                    <h6 className="fw-semibold mb-2" style={{ fontSize: 13, color: '#555', textTransform: 'uppercase', letterSpacing: 1 }}>Employees</h6>
                                    <div style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
                                        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                                            <thead style={{ background: '#f8f9fa' }}>
                                                <tr>
                                                    <th>Employee</th>
                                                    <th>Pay Type</th>
                                                    <th>{empPayLabel()}</th>
                                                    {reason === 'termination' && <th>Final Hours</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedEmployees.map(emp => {
                                                    const p = empPayments[emp.id] || {};
                                                    return (
                                                        <tr key={emp.id}>
                                                            <td className="fw-medium">{emp.firstName} {emp.lastName}</td>
                                                            <td style={{ textTransform: 'capitalize' }}>{emp.payType || 'salary'}</td>
                                                            <td>
                                                                <input type="number" className="form-control form-control-sm" style={{ width: 120 }}
                                                                    placeholder="0.00"
                                                                    value={
                                                                        reason === 'bonus'        ? (p.bonus || '') :
                                                                        reason === 'commission'   ? (p.commission || '') :
                                                                        isReimbType               ? (p.reimbursement || '') :
                                                                        (p.hours || '')
                                                                    }
                                                                    onChange={e => {
                                                                        const f = reason === 'bonus' ? 'bonus' : reason === 'commission' ? 'commission' : isReimbType ? 'reimbursement' : 'hours';
                                                                        setEmpPay(emp.id, f, e.target.value);
                                                                    }} />
                                                            </td>
                                                            {reason === 'termination' && (
                                                                <td>
                                                                    <input type="number" className="form-control form-control-sm" style={{ width: 100 }}
                                                                        placeholder="0" value={p.hours || ''}
                                                                        onChange={e => setEmpPay(emp.id, 'hours', e.target.value)} />
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {/* Contractor payment table */}
                            {selectedContractors.length > 0 && (
                                <>
                                    <h6 className="fw-semibold mb-2" style={{ fontSize: 13, color: '#555', textTransform: 'uppercase', letterSpacing: 1 }}>Contractors</h6>
                                    <div style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
                                        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                                            <thead style={{ background: '#f8f9fa' }}>
                                                <tr>
                                                    <th>Contractor</th>
                                                    <th>Pay Type</th>
                                                    <th>{contPayLabel()}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedContractors.map(c => {
                                                    const p = contPayments[c.id] || {};
                                                    return (
                                                        <tr key={c.id}>
                                                            <td className="fw-medium">{c.firstName} {c.lastName}{c.businessName ? ` (${c.businessName})` : ''}</td>
                                                            <td style={{ textTransform: 'capitalize' }}>{c.payType || 'FlatFee'}</td>
                                                            <td>
                                                                <input type="number" className="form-control form-control-sm" style={{ width: 120 }}
                                                                    placeholder="0.00"
                                                                    value={isReimbType ? (p.reimbursement || '') : (p.amount || '')}
                                                                    onChange={e => setContPay(c.id, isReimbType ? 'reimbursement' : 'amount', e.target.value)} />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            <div className="d-flex justify-content-between mt-2">
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setStep(STEPS.SELECT)}>Back</button>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{ backgroundColor: '#1B75F0', color: '#fff' }}
                                    disabled={updating || loadingPreview}
                                    onClick={() => doUpdate()}
                                >
                                    {updating || loadingPreview ? 'Loading...' : 'Next: Preview'}
                                </button>
                            </div>
                        </>
                    )}

                    {/* ── STEP 3: Preview & Approve ── */}
                    {step === STEPS.PREVIEW && (
                        <>
                            {approved ? (
                                <div className="text-center py-4">
                                    <div style={{ fontSize: 48 }}>✓</div>
                                    <h5 className="fw-bold mt-3">Off-Cycle Payroll Approved!</h5>
                                    <p className="text-muted">The payroll has been approved and recorded.</p>
                                    <button type="button" className="btn btn-sm" style={{ backgroundColor: '#1B75F0', color: '#fff' }} onClick={onClose}>
                                        Close
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <span className="badge" style={{ background: '#fff3cd', color: '#856404', fontSize: 13, fontWeight: 500, padding: '5px 12px', borderRadius: 20 }}>
                                            Reason: {reasonLabel}
                                        </span>
                                    </div>

                                    {/* Summary cards */}
                                    {previewData && (() => {
                                        const selEmps  = (previewData.employees  || []).filter(e => selectedEmpIds.includes(e.employeeId));
                                        const selConts = (previewData.contractors || []).filter(c => selectedContIds.includes(c.contractorId));
                                        const empGross = selEmps.reduce((s, e) => s + (e.grossPay || 0), 0);
                                        const contGross = selConts.reduce((s, c) => s + (c.grossPay || 0) + (c.reimbursement || 0), 0);
                                        const sumGross = empGross + contGross;
                                        const sumTaxes = selEmps.reduce((s, e) => s + ((e.federalIncomeTax || 0) + (e.stateIncomeTax || 0) + (e.socialSecurityTax || 0) + (e.medicareTax || 0)), 0);
                                        const sumDed   = selEmps.reduce((s, e) => s + ((e.preTaxDeductions || 0) + (e.postTaxDeductions || 0)), 0);
                                        const sumNet   = selEmps.reduce((s, e) => s + (e.netPay || 0), 0) + contGross;
                                        return (
                                            <div className="row g-3 mb-4">
                                                {[
                                                    { label: 'Gross Pay',   value: sumGross, color: '#198754' },
                                                    { label: 'Total Taxes', value: sumTaxes, color: '#1B75F0' },
                                                    { label: 'Deductions',  value: sumDed,   color: '#dc3545' },
                                                    { label: 'Net Pay',     value: sumNet,   color: '#0d6efd' },
                                                ].map(card => (
                                                    <div key={card.label} className="col-6 col-md-3">
                                                        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', borderLeft: `4px solid ${card.color}` }}>
                                                            <p className="mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#888' }}>{card.label}</p>
                                                            <p className="mb-0 fw-bold" style={{ fontSize: 18, color: card.color }}>{fmt(card.value)}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}

                                    {/* Employee breakdown */}
                                    {previewData?.employees?.filter(e => selectedEmpIds.includes(e.employeeId)).length > 0 && (
                                        <div className="mb-4">
                                            <h6 className="fw-semibold mb-2">Employee Breakdown</h6>
                                            <div style={{ overflowX: 'auto' }}>
                                                <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                                    <thead style={{ background: '#f8f9fa' }}>
                                                        <tr>
                                                            <th>Employee</th>
                                                            <th className="text-end">Gross Pay</th>
                                                            <th className="text-end">Taxes</th>
                                                            <th className="text-end">Net Pay</th>
                                                            <th>Payment Method</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {previewData.employees
                                                            .filter(e => selectedEmpIds.includes(e.employeeId))
                                                            .map(emp => (
                                                                <tr key={emp.employeeId}>
                                                                    <td className="fw-medium">{emp.employeeFirstName} {emp.employeeLastName}</td>
                                                                    <td className="text-end">{fmt(emp.grossPay)}</td>
                                                                    <td className="text-end" style={{ color: '#1B75F0' }}>{fmt((emp.federalIncomeTax || 0) + (emp.stateIncomeTax || 0) + (emp.socialSecurityTax || 0) + (emp.medicareTax || 0))}</td>
                                                                    <td className="text-end fw-semibold text-success">{fmt(emp.netPay)}</td>
                                                                    <td style={{ textTransform: 'capitalize' }}>{emp.paymentMethod || 'check'}</td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Contractor breakdown */}
                                    {previewData?.contractors?.filter(c => selectedContIds.includes(c.contractorId)).length > 0 && (
                                        <div className="mb-4">
                                            <h6 className="fw-semibold mb-2">Contractor Breakdown</h6>
                                            <div style={{ overflowX: 'auto' }}>
                                                <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                                    <thead style={{ background: '#f8f9fa' }}>
                                                        <tr>
                                                            <th>Contractor</th>
                                                            <th className="text-end">Gross Pay</th>
                                                            <th className="text-end">Reimbursement</th>
                                                            <th className="text-end">Total</th>
                                                            <th>Payment Method</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {previewData.contractors
                                                            .filter(c => selectedContIds.includes(c.contractorId))
                                                            .map(c => {
                                                                const gross = c.grossPay ?? 0;
                                                                const reimb = c.reimbursement ?? 0;
                                                                return (
                                                                    <tr key={c.contractorId}>
                                                                        <td className="fw-medium">{c.firstName} {c.lastName}{c.businessName ? ` (${c.businessName})` : ''}</td>
                                                                        <td className="text-end">{fmt(gross)}</td>
                                                                        <td className="text-end">{reimb > 0 ? fmt(reimb) : '-'}</td>
                                                                        <td className="text-end fw-semibold text-success">{fmt(gross + reimb)}</td>
                                                                        <td style={{ textTransform: 'capitalize' }}>{c.paymentMethod || 'check'}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {!previewData && (
                                        <p className="text-muted text-center py-3">No preview data available.</p>
                                    )}

                                    <div className="d-flex justify-content-between mt-4">
                                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setStep(STEPS.PAYMENTS)}>Back</button>
                                        <button
                                            type="button"
                                            className="btn btn-sm"
                                            style={{ backgroundColor: '#28a745', color: '#fff' }}
                                            disabled={approving}
                                            onClick={() => doApprove()}
                                        >
                                            {approving ? 'Approving...' : 'Approve Off-Cycle Payroll'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
